import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAiNews, getTrendingModels, HF_TRENDING, HN_SEARCH, NewsResult, timeAgo, TrendingResult } from '../utils/news';
import { getOpenRouterModels, OpenRouterResult } from '../utils/api';
import { orCountry, orPrefix } from '../utils/modelFilters';
import { OPENROUTER_API } from '../utils/sourceIndex';
import { countryNames } from '../data/geography';
import { formatCompact, formatInt } from '../utils/format';
import { PageHeader } from '../components/PageHeader';
import { PageSources } from '../components/PageSources';
import { DataBadge } from '../components/DataBadge';
import { SourceRef } from '../components/SourceRef';
const DAY = 86_400_000;
export function Latest() {
  const [news, setNews] = useState<NewsResult | null>(null);
  const [trending, setTrending] = useState<TrendingResult | null>(null);
  const [or, setOr] = useState<OpenRouterResult | null>(null);
  useEffect(() => {
    let alive = true;
    getAiNews().then(r => alive && setNews(r));
    getTrendingModels().then(r => alive && setTrending(r));
    getOpenRouterModels().then(r => alive && setOr(r));
    return () => {
      alive = false;
    };
  }, []);
  const fresh = useMemo(() => {
    const cutoff = (Date.now() - 30 * DAY) / 1000;
    return (or?.models ?? []).filter(m => m.created != null && m.created >= cutoff).sort((a, b) => (b.created ?? 0) - (a.created ?? 0)).slice(0, 20);
  }, [or]);
  return <>
      <PageHeader title="Latest" lede="What changed in the machine population today: the AI stories people are discussing, models that went live in the last 30 days, and what’s trending among open models." data-id="element-1186" data-mp="3l4y2dac6fo6a" />
      <PageSources data-id="element-1187" data-mp="3catft342sywk">
        <DataBadge mode={news ? news.mode === 'unavailable' ? 'snapshot' : news.mode : 'loading'} source="Hacker News (Algolia) · AI stories" asOf={news?.asOf || undefined} data-id="element-1188" data-mp="3c98k3a60z9x4" />
        <DataBadge mode={or ? or.mode : 'loading'} source="OpenRouter · new models" asOf={or?.asOf} data-id="element-1189" data-mp="7vjwtsy8ct26" />
        <DataBadge mode={trending ? trending.mode === 'unavailable' ? 'snapshot' : trending.mode : 'loading'} source="Hugging Face · trending" asOf={trending?.asOf || undefined} data-id="element-1190" data-mp="225gx1yiqvcej" />
      </PageSources>

      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 pt-10 md:px-8 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]" data-id="element-1191" data-mp="1sem82me45ype">
        <section aria-labelledby="news-title" data-id="element-1192" data-mp="1xbvwbypro4bn">
          <h2 id="news-title" className="font-serif text-2xl text-ink md:text-3xl" data-id="element-1193" data-mp="1jdfqe9zqahuk">
            In the news
            <SourceRef href={HN_SEARCH} label="Hacker News search (Algolia)" grade="B" data-id="element-1194" data-mp="1oebuoonzs60i" />
          </h2>
          <p className="mt-1 text-[13.5px] text-muted" data-id="element-1195" data-mp="19hl1wy8tpsjv">Newest AI, model and agent stories with real discussion, from Hacker News.</p>
          {!news && <p className="py-12 text-center font-mono text-xs text-muted" role="status" data-id="element-1196" data-mp="33ukf6kpqeanz">
              Gathering stories…
            </p>}
          {news?.mode === 'unavailable' && <p className="mt-6 rounded-lg border border-line px-4 py-8 text-center text-sm text-muted" data-id="element-1197" data-mp="38r1d26mqg3e9">Stories can’t be loaded right now. Try again in a minute.</p>}
          <ol className="mt-5 divide-y divide-line border-y border-line" data-id="element-1198" data-mp="s8jxk9ob2ypt">
            {(news?.items ?? []).map(n => <li key={n.id} className="py-3.5" data-id="element-1199" data-mp="16xy8vnbfzq7u">
                <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-[15px] leading-snug text-ink hover:text-machine" data-id="element-1200" data-mp="124v92xg2nkj7">
                  {n.title}
                </a>
                <p className="mt-1 flex flex-wrap gap-x-3 font-mono text-[11.5px] text-faint" data-id="element-1201" data-mp="1bvhmqm48a0z9">
                  <span data-id="element-1202" data-mp="1pv96z4lc8dlp">{n.domain}</span>
                  <span data-id="element-1203" data-mp="1pu7jg0n4xyn3">{timeAgo(n.created)}</span>
                  <span data-id="element-1204" data-mp="1kvwads4nzs5x">{formatInt(n.points)} points</span>
                  <a href={n.discussUrl} target="_blank" rel="noopener noreferrer" className="hover:text-machine" data-id="element-1205" data-mp="1kwfosyv3vyf7">
                    {formatInt(n.comments)} comments
                  </a>
                </p>
              </li>)}
          </ol>
        </section>

        <div className="space-y-14" data-id="element-1206" data-mp="3o3dd19ys2duh">
          <section aria-labelledby="new-models-title" data-id="element-1207" data-mp="3o69ev9dqxmo7">
            <h2 id="new-models-title" className="font-serif text-2xl text-ink" data-id="element-1208" data-mp="3t288bbdxluvh">
              New models, last 30 days
              <SourceRef href={OPENROUTER_API} label="OpenRouter models API" grade="A" data-id="element-1209" data-mp="1xhtmhl316qx" />
            </h2>
            <p className="mt-1 text-[13.5px] text-muted" data-id="element-1210" data-mp="6v9wu7r6gfaf">{or ? `${fresh.length} models went live on OpenRouter this month.` : 'Loading…'}</p>
            <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-1211" data-mp="3acibkiqlyz0x">
              {fresh.map(m => {
              const cc = orCountry(m);
              return <li key={m.id} className="flex items-baseline justify-between gap-4 py-2.5" data-id="element-1212" data-mp="3fa0arhetpzvq">
                    <div className="min-w-0" data-id="element-1213" data-mp="3k4ed63rwl79w">
                      <a href={`https://openrouter.ai/${m.id.replace(/:free$/, '')}`} target="_blank" rel="noopener noreferrer" className="block truncate text-[14px] text-ink hover:text-machine" data-id="element-1214" data-mp="2mm4iqq078w5a">
                        {m.name}
                      </a>
                      <p className="font-mono text-[11px] text-faint" data-id="element-1215" data-mp="18otdg6xc4om4">
                        {orPrefix(m)}
                        {cc && <>
                            {' · '}
                            <Link to={`/countries/${cc}`} className="hover:text-machine" data-id="element-1216" data-mp="urn04rvdri4a">
                              {countryNames[cc] ?? cc}
                            </Link>
                          </>}
                        {m.free && <span className="ml-2 text-live" data-id="element-1217" data-mp="l0s3ctkid04l">free</span>}
                      </p>
                    </div>
                    <span className="shrink-0 font-mono text-[11.5px] text-muted" data-id="element-1218" data-mp="h083vjcgpkfa">{m.created ? new Date(m.created * 1000).toISOString().slice(0, 10) : ''}</span>
                  </li>;
            })}
              {or && fresh.length === 0 && <li className="py-6 text-center text-[13px] text-muted" data-id="element-1219" data-mp="227dt5y9m2t2">No new models in the last 30 days.</li>}
            </ul>
            <Link to="/models" className="mt-3 inline-block text-[13px] text-muted hover:text-machine" data-id="element-1220" data-mp="3ojg7iofc2efx">
              All models →
            </Link>
          </section>

          <section aria-labelledby="trending-title" data-id="element-1221" data-mp="3fkb69ace0702">
            <h2 id="trending-title" className="font-serif text-2xl text-ink" data-id="element-1222" data-mp="3flm7p3id12o0">
              Trending open models
              <SourceRef href={HF_TRENDING} label="Hugging Face trending models" grade="A" data-id="element-1223" data-mp="3jn7ubgkp4vbe" />
            </h2>
            <p className="mt-1 text-[13.5px] text-muted" data-id="element-1224" data-mp="g4obf4nwr1t9">What the open-model community is downloading and liking right now.</p>
            {trending?.mode === 'unavailable' && <p className="mt-4 text-[13px] text-muted" data-id="element-1225" data-mp="2crln79pp401">Trending list can’t be loaded right now.</p>}
            <ol className="mt-4 divide-y divide-line border-y border-line" data-id="element-1226" data-mp="6dbjye60udnr">
              {(trending?.models ?? []).slice(0, 20).map((m, i) => <li key={m.id} className="grid grid-cols-[1.75rem_minmax(0,1fr)_auto] items-baseline gap-3 py-2.5" data-id="element-1227" data-mp="yuxd8dfs8rkf" data-mpi={i}>
                  <span className="font-mono text-[11.5px] text-faint" data-id="element-1228" data-mp="2jkt031bbol8l">{i + 1}</span>
                  <div className="min-w-0" data-id="element-1229" data-mp="2emrjcn1dcy0a">
                    <a href={`https://huggingface.co/${m.id}`} target="_blank" rel="noopener noreferrer" className="block truncate font-mono text-[12.5px] text-ink hover:text-machine" data-id="element-1230" data-mp="2elyh9zdcuwoh">
                      {m.id}
                    </a>
                    {m.task && <p className="font-mono text-[10.5px] text-faint" data-id="element-1231" data-mp="20mgnf5l7ct89">{m.task}</p>}
                  </div>
                  <span className="font-mono text-[11.5px] text-muted" data-id="element-1232" data-mp="1vo5dyvytkkqk">♥ {formatCompact(m.likes)}</span>
                </li>)}
            </ol>
          </section>
        </div>
      </div>
    </>;
}